/**
 * Scroll Manager for handling edit-and-return navigation
 * Saves and restores scroll positions when navigating to edit forms
 */

export interface ScrollPosition {
  anchor: string;
  scrollTop: number;
  timestamp: number;
}

export class ScrollManager {
  private static readonly STORAGE_KEY = 'leanai_return_anchor';
  private static readonly STORAGE_KEY_SCROLL = 'leanai_scroll_position';
  private static readonly STORAGE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Save current scroll position and prepare for edit navigation
   */
  static saveAndNavigate(editUrl: string, anchorId: string): void {
    const scrollPosition: ScrollPosition = {
      anchor: anchorId,
      scrollTop: window.scrollY,
      timestamp: Date.now()
    };

    // Store both anchor and scroll position
    sessionStorage.setItem(this.STORAGE_KEY, anchorId);
    sessionStorage.setItem(this.STORAGE_KEY_SCROLL, JSON.stringify(scrollPosition));

    // Navigate to edit page
    window.location.href = editUrl;
  }

  /**
   * Restore scroll position after returning from edit
   */
  static restorePosition(): boolean {
    try {
      const anchorId = sessionStorage.getItem(this.STORAGE_KEY);
      const scrollData = sessionStorage.getItem(this.STORAGE_KEY_SCROLL);

      if (!anchorId || !scrollData) {
        return false;
      }

      const scrollPosition: ScrollPosition = JSON.parse(scrollData);

      // Check if data is not too old (TTL check)
      if (Date.now() - scrollPosition.timestamp > this.STORAGE_TTL) {
        this.clearStoredPosition();
        return false;
      }

      // Try to scroll to element first
      const element = document.querySelector(anchorId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Clear storage after successful restore
        this.clearStoredPosition();
        return true;
      }

      // Fallback to scroll position
      window.scrollTo({ top: scrollPosition.scrollTop, behavior: 'smooth' });
      this.clearStoredPosition();
      return true;

    } catch (error) {
      console.warn('Failed to restore scroll position:', error);
      this.clearStoredPosition();
      return false;
    }
  }

  /**
   * Clear stored position data
   */
  static clearStoredPosition(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem(this.STORAGE_KEY_SCROLL);
  }

  /**
   * Check if there's a pending return navigation
   */
  static hasPendingReturn(): boolean {
    const anchorId = sessionStorage.getItem(this.STORAGE_KEY);
    const scrollData = sessionStorage.getItem(this.STORAGE_KEY_SCROLL);
    
    if (!anchorId || !scrollData) {
      return false;
    }

    try {
      const scrollPosition: ScrollPosition = JSON.parse(scrollData);
      return Date.now() - scrollPosition.timestamp <= this.STORAGE_TTL;
    } catch {
      return false;
    }
  }

  /**
   * Generate unique anchor ID for a given entity
   */
  static generateAnchorId(type: 'problem' | 'hypothesis' | 'project', id: string): string {
    return `#${type}-${id}`;
  }

  /**
   * Generate edit URL with return context
   */
  static generateEditUrl(type: 'problem' | 'hypothesis' | 'project', id: string): string {
    const baseUrls = {
      problem: `/app-problems/${id}?edit=true`,
      hypothesis: `/app-hypotheses/${id}?edit=true`,
      project: `/app-projects?view=detail&id=${id}&action=edit&returnToReport=true`
    };
    
    return baseUrls[type];
  }
}